import React, { createContext, useCallback, useContext, useState } from "react";
import {
  getStudents,
  getTeachers,
  createTeacher,
  createStudent,
  updateTeacher,
  updateStudent,
  deleteUserService,
  getUserById,
  getAsignaturas,
  getSalones,
  getStudentsByDirector,
  getSalonByStudent,
  getCoursesByTeacher,
  getSalonByDirector,
} from "../services/usersService";

import { obtenerAsignaturasSalon } from "../services/notasService";


const UsersContext = createContext();

export const useUsers = () => useContext(UsersContext);

export const UsersProvider = ({ children }) => {
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [salones, setSalones] = useState([]); // Estado para salones
  const [salonGrado, setSalonGrado] = useState(null); // Para almacenar el grado del salón del director
  const [asignaturas, setAsignaturas] = useState([]); // Estado para asignaturas
  const [salonesLoaded, setSalonesLoaded] = useState(false);
  const [asignaturasLoaded, setAsignaturasLoaded] = useState(false);
  const [courses, setCourses] = useState(null);
  const [salon, setSalon] = useState(null);
  const [studentsLoaded, setStudentsLoaded] = useState(false);
  const [teachersLoaded, setTeachersLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ AGREGAR: Estado para asignaturas del salón
  const [asignaturasSalon, setAsignaturasSalon] = useState([]);
  const [loadingAsignaturasSalon, setLoadingAsignaturasSalon] = useState(false);


  const fetchStudents = useCallback(
    async (force = false) => {
      if (!studentsLoaded || force) {
        setLoading(true);
        try {
          const data = await getStudents();
          setStudents(data || []);
          setStudentsLoaded(true);
        } catch (error) {
          
        } finally {
          setLoading(false);
        }
      }
    },
    [studentsLoaded]
  );

  const fetchTeachers = useCallback(
    async (force = false) => {
      if (!teachersLoaded || force) {
        setLoading(true);
        try {
          const data = await getTeachers();
          setTeachers(data || []);
          setTeachersLoaded(true);
        } catch (error) {
          
        } finally {
          setLoading(false);
        }
      }
    },
    [teachersLoaded]
  );

  const fetchSalones = useCallback(
    async (force = false) => {
      if (!salonesLoaded || force) {
        try {
          const data = await getSalones();
          setSalones(data || []);
          setSalonesLoaded(true);
        } catch (error) {
          
        }
      }
    },
    [salonesLoaded]
  );

  const fetchSalonForDirector = useCallback(async () => {
    try {
      const salon = await getSalonByDirector();
      // 
      setSalonGrado(salon || null); // Grado del salón asociado al director
    } catch (error) {
      
      setSalonGrado(null); // 👈 muy importante para evitar bucles
    }
  }, []);

  const fetchAsignaturas = useCallback(
    async (force = false) => {
      if (!asignaturasLoaded || force) {
        try {
          const data = await getAsignaturas();
          setAsignaturas(data || []);
          setAsignaturasLoaded(true);
          // 
        } catch (error) {
          
        }
      }
    },
    [asignaturasLoaded]
  );

  const getUser = useCallback(
    async (id) => {
      const teacher = teachers.find((t) => t.user_id === id);
      const student = students.find((s) => s.user_id === id);
      if (teacher || student) {
        return teacher || student;
      }
      return await getUserById(id);
    },
    [teachers, students]
  );

  const fetchStudentsByDirector = async (force = false) => {
    if (!studentsLoaded || force) {
      setLoading(true);
      try {
        setLoading(true);
        const data = await getStudentsByDirector();
        // 
        setStudents(data || []);
      } catch (error) {
        
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchCoursesForTeacher = useCallback(async () => {
    if (courses !== null) return; // Evitar múltiples llamadas
  
    setLoading(true);
    try {
      const response = await getCoursesByTeacher();
      if (response && response.data) {
        setCourses(response.data);
      } else {
        setCourses([]); // Evita que se vuelva a ejecutar
      }
    } catch (error) {
      
      setCourses([]); // Evita bucles infinitos en caso de error
    } finally {
      setLoading(false);
    }
  }, [courses]); // Solo ejecuta si `courses` es null
  
  

  const fetchSalonForStudent = async () => {
    if (!salon) {
      setLoading(true);
      try {
        const response = await getSalonByStudent();
        setSalon(response.data || null);
      } catch (error) {
        
      } finally {
        setLoading(false);
      }
    }
  };

  const createNewTeacher = async (data) => {
    const response = await createTeacher(data);
    await fetchTeachers(true);
    return response;
  };

  const createNewStudent = async (data) => {
    const response = await createStudent(data);
    await fetchStudents(true);
    return response;
  };

  const updateTeacherData = async (id, data) => {
    try {
      setLoading(true);
      const response = await updateTeacher(id, data); // Llama a la función del servicio
      // 
      await fetchTeachers(true);
      return response;
    } catch (error) {
      
      throw error; // Asegúrate de manejar el error en la función que llama a esto
    } finally {
      setLoading(false);
    }
  };

  const updateStudentData = async (id, data) => {
    try {
      setLoading(true);
      const response = await updateStudent(id, data); // Llama a la función del servicio
      // 
      await fetchStudents(true);
      return response;
    } catch (error) {
      
      throw error; // Asegúrate de manejar el error en la función que llama a esto
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    try {
      await deleteUserService(userId);
      // Después de eliminar, puedes volver a cargar la lista de usuarios
      if (teachers.some((teacher) => teacher.user_id === userId)) {
        await fetchTeachers(true);
      } else if (students.some((student) => student.user_id === userId)) {
        await fetchStudents(true);
      }
      return { success: true };
    } catch (error) {
      
      return { success: false, error };
    }
  };

  // ✅ NUEVA: Función para obtener asignaturas del salón
  const fetchAsignaturasSalon = useCallback(async (salonId) => {
    if (!salonId) return;
    
    setLoadingAsignaturasSalon(true);
    try {
      const response = await obtenerAsignaturasSalon(salonId);
      setAsignaturasSalon(response.data || []);
    } catch (error) {
      console.error("Error fetching asignaturas salon:", error);
      setAsignaturasSalon([]);
    } finally {
      setLoadingAsignaturasSalon(false);
    }
  }, []);

  return (
    <UsersContext.Provider
      value={{
        students,
        teachers,
        getUser,
        salonGrado,
        asignaturasSalon,
        loadingAsignaturasSalon,
        fetchAsignaturasSalon,
        createNewTeacher,
        createNewStudent,
        updateTeacherData,
        updateStudentData,
        deleteUser,
        fetchStudents,
        fetchTeachers,
        fetchSalones,
        fetchAsignaturas,
        fetchStudentsByDirector,
        fetchCoursesForTeacher,
        fetchSalonForStudent,
        fetchSalonForDirector,
        salones,
        asignaturas,
        teachersLoaded,
        courses,
        salon,
        loading,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};
